import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./baseQuery";
import { API } from "./constant";
import { toast } from "react-toastify";

export const financeApi = createApi({
  baseQuery,
  reducerPath: "financeApii",
  tagTypes: ["ServiceSetup", "booking", "survey", "retirement"],
  endpoints: (builder) => ({
    FinancePost: builder.mutation({
      query: (req) => ({
        url: req.path,
        method: "POST",
        body: req.datas,
      }),
      transformErrorResponse: (err) => {
        if (err.status === "FETCH_ERROR") {
          toast.error("Network Error");
        }

        if (err.status === 404 && !err?.data?.statusMessage) {
          toast.error("invalid API", "error");
        } else if (err.status === 500) {
          toast.error(
            "Something went wrong. It’s not you, it’s us. Please give it another try.",
            "error",
          );
        } else if (err.status === 401) {
          toast.error("session has expired login, please login to continue ");
          localStorage.removeItem("token");
          window.location.replace("/");
        }
        return err;
      },
    }),
    FinanceGet: builder.query({
      query: (queries) => queries,
      transformErrorResponse: (err) => {
        if (err.status === "FETCH_ERROR") {
          toast.error("Network Error");
        } else if (err.status === 500) {
          toast.error(
            "Something went wrong. It’s not you, it’s us. Please give it another try.",
          );
        } else if (err.status === 401) {
          toast.error("session has expired login, please login to continue ");
          localStorage.removeItem("token");
          window.location.replace("/");
        }
        return err;
      },
    }),
    getBookingServiceSetup: builder.query({
      query: ({ ...values }) => ({
        url: API.BOOKING_SERVICE_SETUP,
        params: { ...values },
      }),
      providesTags: ["ServiceSetup", "booking"],
    }),

    getAccountCardDetails: builder.query({
      query: ({ ...values }) => ({
        url: API.CARD_SETUP,
        params: { ...values },
      }),
      providesTags: ["CardSetup", "booking"],
    }),

    getSingleService: builder.query({
      query: (id) => ({
        url: `${API.SINGLE_SERVICE}/${id}`,
      }),
      providesTags: ["ServiceSetup"],
    }),

    setupBookService: builder.mutation({
      query: ({ ...values }) => ({
        url: API.SETUP_BOOK_SERVICE,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["ServiceSetup"],
    }),

    setupAccount: builder.mutation({
      query: ({ ...values }) => ({
        url: API.CREATE_CARD_SETUP,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["ServiceSetup"],
    }),

    setupUpdateAccount: builder.mutation({
      query: ({ ...values }) => ({
        url: API.CREATE_CARD_SETUP_UPDATE,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["ServiceSetup"],
    }),

    setupFacility: builder.mutation({
      query: ({ ...values }) => ({
        url: API.FACILITY_SETUP,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["ServiceSetup"],
    }),

    getBookingServices: builder.query({
      query: ({ ...values }) => ({
        url: API.GET_BOOKING_SERVICES,
        params: { ...values },
      }),
      providesTags: ["booking"],
    }),

    createBookingService: builder.mutation({
      query: ({ ...values }) => ({
        url: API.CREATE_BOOKING_SERVICES,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["booking"],
    }),

    getBookings: builder.query({
      query: ({ ...values }) => ({
        url: API.GET_BOOKINGS,
        params: { ...values },
      }),
      providesTags: ["booking"],
    }),

    getBookingServiceDetails: builder.query({
      query: (id) => ({
        url: API.GET_BOOKING_SERVICE_DETAILS,
        params: id,
      }),
      providesTags: ["ServiceSetup"],
    }),

    updateBookService: builder.mutation({
      query: ({ ...values }) => ({
        url: API.UPDATE_BOOK_SERVICE,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["ServiceSetup", "booking"],
    }),

    deleteBookService: builder.mutation({
      query: ({ id }) => ({
        url: API.DELETE_BOOK_SERVICE,
        method: "POST",
        body: { id },
      }),
      invalidatesTags: ["ServiceSetup", "booking"],
    }),

    declineAccountSetup: builder.mutation({
      query: ({ id }) => ({
        url: API.DECLINE_ACCOUNT_SETUP,
        method: "POST",
        body: { id },
      }),
      invalidatesTags: ["ServiceSetup", "booking"],
    }),
    approveAccountSetup: builder.mutation({
      query: ({ id }) => ({
        url: API.APPROVE_ACCOUNT_SETUP,
        method: "POST",
        body: { id },
      }),
      invalidatesTags: ["ServiceSetup", "booking"],
    }),
    deleteBookingFacility: builder.mutation({
      query: ({ id }) => ({
        url: API.DELETE_BOOKING_FACILITY,
        method: "POST",
        body: { id },
      }),
      invalidatesTags: ["ServiceSetup", "booking"],
    }),
    addComponent: builder.mutation({
      query: ({ ...values }) => ({
        url: API.ADD_COMPONENT,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["ServiceSetup"],
    }),
    getBookingComponent: builder.query({
      query: ({ ...values }) => ({
        url: API.GET_COMPONENTS,
        params: { ...values },
      }),
      providesTags: ["ServiceSetup"],
    }),
    approveBookings: builder.mutation({
      query: ({ ...values }) => ({
        url: API.APPROVE_BOOKINGS,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["ServiceSetup"],
    }),

    declineBookings: builder.mutation({
      query: ({ ...values }) => ({
        url: API.DECLINE_BOOKINGS,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["ServiceSetup"],
    }),

    viewBookingDetails: builder.query({
      query: (id) => ({
        url: `BookingService/${id}`,
        method: "GET", // Explicitly mention the HTTP method (optional but clearer)
      }),
      providesTags: (result, error, id) => [{ type: "BookingDetails", id }],
      keepUnusedDataFor: 60, // Cache the result for 60 seconds
    }),

    // BookingFacilitySetup/1
    getBookingFacilityDetails: builder.query({
      query: (id) => ({
        url: `BookingFacilitySetup/${id}`,
        method: "GET", // Explicitly mention the HTTP method (optional but clearer)
      }),

      providesTags: (result, error, id) => [
        { type: "BookingFacilityDetails", id },
      ],
      keepUnusedDataFor: 60, // Cache the result for 60 seconds
    }),

    getBookingFacility: builder.query({
      query: ({ ...values }) => ({
        url: API.GET_BOOKING_FACILITY,
        params: { ...values },
      }),
      providesTags: ["ServiceSetup", "booking"],
    }),
    createSurvey: builder.mutation({
      query: ({ ...values }) => ({
        url: API.FINANCE.CREATE_SURVEY,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["survey"],
    }),
    getFinanceSurvey: builder.query({
      query: ({
        CategoryType,
        Status,
        PageSize,
        PageNumber,
        StartDate,
        EndDate,
      }) => ({
        url: `${API.FINANCE.CREATE_SURVEY}/all`,
        params: {
          CategoryType,
          Status,
          PageSize,
          PageNumber,
          StartDate,
          EndDate,
        },
      }),
      providesTags: ["survey"],
    }),
    getFinanceSurveyById: builder.query({
      query: (id) => ({
        url: `${API.FINANCE.CREATE_SURVEY}/${id}`,
      }),
      providesTags: ["survey"],
    }),
    updateSurvey: builder.mutation({
      query: ({ ...values }) => ({
        url: `${API.FINANCE.CREATE_SURVEY}/update`,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["survey"],
    }),
    deleteSurvey: builder.mutation({
      query: (ids) => ({
        url: `${API.FINANCE.CREATE_SURVEY}/delete`,
        method: "POST",
        body: ids,
      }),
      invalidatesTags: ["survey"],
    }),
    approveSurvey: builder.mutation({
      query: ({ ...values }) => ({
        url: `${API.FINANCE.CREATE_SURVEY}/approve`,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["survey"],
    }),
    declineSurvey: builder.mutation({
      query: ({ ...values }) => ({
        url: `${API.FINANCE.CREATE_SURVEY}/reject`,
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["survey"],
    }),
    submitRetirement: builder.mutation({
      query: (values) => ({
        url: API.FINANCE.SUBMIT_EMPLOYEE_SURVEY,
        method: "POST",
        body: values,
      }),
      invalidatesTags: ["survey"],

    }),
    getAllRetirement: builder.query({
      query: ({FloatType,PageSize,PageNumber, Status, StartDate, EndDate}) => ({
        url: `${API.FINANCE.GET_RETIREMENT}`,
        params: {FloatType,PageSize,PageNumber, Status, StartDate, EndDate}
      }),
      providesTags: ["retirement", "survey"],
    }),
    getRetirementById : builder.query({
      query : (id) => ({
        url : `${API.FINANCE.GET_RETIREMENT_BY_ID}/${id}`
      }),
      providesTags : ["retirement", "survey"]
    }),
    approveFloatRetirement: builder.mutation({
      query : ({...values}) => ({
        url : `${API.FINANCE.APPROVE_FLOAT_RETIREMENT}`,
        method : "POST",
        body : {...values}
      })
    }),
    rejectFloatRetirement: builder.mutation({
      query : ({...values}) => ({
        url : `${API.FINANCE.REJECT_FLOAT_RETIREMENT}`,
        method : "POST",
        body : {...values}
      })
    }),
    previewSurvey: builder.query({
      query : (CategoryType) => ({
        url : `${API.FINANCE.PREVIEW_SURVEY}?CategoryType=${CategoryType}`
      })
    })
  }),
});

export const {
  useGetBookingServiceSetupQuery,
  useSetupBookServiceMutation,
  useSetupFacilityMutation,
  useGetBookingServicesQuery,
  useGetBookingsQuery,
  useUpdateBookServiceMutation,
  useDeleteBookServiceMutation,
  useDeleteBookingFacilityMutation,
  useGetSingleServiceQuery,
  useAddComponentMutation,
  useGetAccountCardDetailsQuery,
  useSetupAccountMutation,
  useSetupUpdateAccountMutation,
  useDeclineAccountSetupMutation,
  useApproveAccountSetupMutation,
  useApproveBookingsMutation,
  useDeclineBookingsMutation,
  useViewBookingDetailsQuery,
  useGetBookingFacilityDetailsQuery,
  useGetBookingFacilityQuery,
  useGetBookingComponentQuery,
  useFinanceGetQuery,
  useFinancePostMutation,
  useCreateSurveyMutation,
  useGetFinanceSurveyQuery,
  useGetFinanceSurveyByIdQuery,
  useUpdateSurveyMutation,
  useDeleteSurveyMutation,
  useApproveSurveyMutation,
  useDeclineSurveyMutation,
  useSubmitRetirementMutation,
  useGetAllRetirementQuery,
  useGetRetirementByIdQuery,
  useApproveFloatRetirementMutation,
  useRejectFloatRetirementMutation,
  usePreviewSurveyQuery
} = financeApi;
